/**
 * 대기 팀 상태 폴링 훅 — 설계 §5 "v1은 폴링(15s), Echo 는 파트너 측만".
 *
 * @see #781 / admin#2111
 */
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ApiClientError } from '@/lib/api';
import { waitlistApi, type WaitlistPartyData } from '@/lib/api/waitlist';
import { logger } from '@/utils/logger';
import { safeErrorMetadata } from '@/utils/safeErrorMetadata';

/** 폴링 주기 — 설계 §5. */
export const WAITLIST_POLL_INTERVAL_MS = 15_000;

export interface UseWaitlistPartyResult {
  party: WaitlistPartyData | null;
  loading: boolean;
  /** 최초 조회 실패 (폴링 중 일시 실패는 여기 반영하지 않는다 — 아래 주석 참조). */
  error: Error | null;
  /** 마지막 정상 상태를 표시 중이지만 최근 조회는 실패했다. */
  stale: boolean;
  /** 현재 최신 상태 조회가 진행 중이다. */
  refreshing: boolean;
  /** 토큰이 서버에 없음(404) — 호출 측이 저장 토큰을 폐기하고 등록 화면으로 되돌린다. */
  notFound: boolean;
  /** 낙관적 갱신 — 도착/이탈 응답을 폴링 주기까지 기다리지 않고 즉시 반영. */
  setParty: (party: WaitlistPartyData) => void;
  refresh: () => Promise<void>;
}

/**
 * 종결 상태 — 더 이상 변하지 않으므로 폴링을 멈춘다.
 *
 * 테이블/픽업 큐 공통이다(#794 확장). 픽업의 `preparing`/`ready` 는 아직 진행 중인
 * 중간 상태이므로 여기 포함하지 않는다 — 페이지(FilaVirtualPage)가 이 두 상태를
 * PrepScreen/PickupReadyScreen 으로 화면만 나눌 뿐, 폴링은 `seated` 전환까지 계속된다.
 */
function isTerminal(party: WaitlistPartyData | null): boolean {
  return party?.status === 'seated' || party?.status === 'cancelled';
}

/**
 * access_token 으로 대기 상태를 15초마다 조회한다.
 *
 * @param accessToken null 이면 폴링하지 않는다 (미등록 상태).
 */
export function useWaitlistParty(accessToken: string | null): UseWaitlistPartyResult {
  const [party, setParty] = useState<WaitlistPartyData | null>(null);
  const [loading, setLoading] = useState(accessToken !== null);
  const [error, setError] = useState<Error | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [stale, setStale] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // 폴링 콜백이 매 렌더 재생성되면 setInterval 이 계속 재설치돼 주기가 밀린다.
  // 최신 party 는 ref 로 읽고, effect 는 accessToken 에만 의존시킨다.
  const partyRef = useRef<WaitlistPartyData | null>(null);
  useLayoutEffect(() => { partyRef.current = party; }, [party]);

  // 폴링-낙관 갱신 경합 방지용 세대 카운터. 도착/이탈 처리는 `setPartyOptimistic` 로
  // 즉시 상태를 반영하는데, 그 요청이 진행되는 동안 시작된(더 이전 시점) 폴링 응답이
  // 나중에 도착하면 방금 반영한 낙관적 상태를 되돌려버릴 수 있다 — 요청 시작 시점의
  // 세대를 캡처해두고, 응답 도착 시 세대가 바뀌었으면(외부 setParty 발생) 폐기한다.
  const generationRef = useRef(0);
  // 정규 폴링과 만료 즉시 refresh가 겹치면 더 늦게 시작한 조회가 권위가 있다.
  // 이전 요청이 뒤늦게 도착해 cancelled를 called로 되돌리는 경합을 막는다.
  const latestRequestRef = useRef(0);
  // 자동 폴링은 진행 중인 조회를 기다린다. 명시적 refresh는 기존처럼 최신 요청이
  // 권위를 가지지만, 다음 자동 tick이 진행 중인 refresh를 무효화하지 않는다.
  const pendingRequestsRef = useRef(new Set<symbol>());
  const notFoundRef = useRef(false);
  const tokenScope = useMemo(() => ({ accessToken }), [accessToken]);
  const activeScopeRef = useRef<typeof tokenScope | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const fetchParty = useCallback(
    async (signal?: AbortSignal) => {
      if (!accessToken || activeScopeRef.current !== tokenScope) {
        return;
      }
      // 수동 refresh도 현재 토큰의 수명에 묶어 전환/unmount 때 취소한다.
      signal ??= controllerRef.current?.signal;
      if (!signal || signal.aborted) {
        return;
      }

      const pendingRequests = pendingRequestsRef.current;
      const pendingRequest = Symbol();
      pendingRequests.add(pendingRequest);
      const requestGeneration = generationRef.current;
      const requestId = latestRequestRef.current + 1;
      latestRequestRef.current = requestId;
      setRefreshing(true);
      if (!partyRef.current) {
        setLoading(true);
        setError(null);
      }

      try {
        const response = await waitlistApi.getParty(accessToken, signal);
        if (
          generationRef.current !== requestGeneration ||
          latestRequestRef.current !== requestId
        ) {
          // 응답을 기다리는 사이 외부(낙관적) setParty 가 발생했다 — 이 응답은 그보다
          // 이전 시점 스냅샷이므로 최신 상태를 덮어쓰지 않도록 폐기한다.
          return;
        }
        setParty(response.data);
        notFoundRef.current = false;
        setNotFound(false);
        setError(null);
        setStale(false);
      } catch (caught) {
        if (caught instanceof DOMException && caught.name === 'AbortError') {
          return;
        }

        if (
          generationRef.current !== requestGeneration ||
          latestRequestRef.current !== requestId
        ) {
          // 위와 동일한 이유로 stale 에러 응답도 폐기한다.
          return;
        }

        if (caught instanceof ApiClientError && caught.status === 404) {
          // 토큰이 서버에 없다 — 만료/삭제/타 환경 토큰. 재시도해도 복구되지 않으므로
          // 폴링을 멈추고 호출 측이 저장 토큰을 지우게 한다.
          notFoundRef.current = true;
          setNotFound(true);
          setError(null);
          setStale(false);
          return;
        }

        // 폴링 중 일시 실패(네트워크 순단 등)는 화면을 에러로 갈아엎지 않는다 —
        // 마지막 성공 상태를 계속 보여주고 다음 주기에 자동 복구한다. 최초 조회
        // (party 없음) 실패만 에러로 노출해 빈 화면을 막는다.
        // Sentry 기본 normalizeDepth=3에서 error.transport는 [Object]가 된다.
        // 정제된 진단 필드만 한 단계 올려 요청 ID와 실패 구간을 보존한다.
        const { transport, ...errorMetadata } = safeErrorMetadata(caught) ?? { type: 'Unknown' };
        const context = { error: errorMetadata, ...(transport ? { transport } : {}), diagnostic_marker: 'menu.waitlist.party-read.v1', operation: 'party-read' };
        if (!partyRef.current) {
          setError(caught instanceof Error ? caught : new Error('Waitlist state unavailable'));
          logger.warn('[waitlist] 초기 상태 조회 실패', { ...context, phase: 'initial' });
        } else {
          setStale(true);
          logger.warn('[waitlist] 폴링 일시 실패 — 마지막 상태 유지', { ...context, phase: 'poll' });
        }
      } finally {
        pendingRequests.delete(pendingRequest);
        if (activeScopeRef.current === tokenScope && latestRequestRef.current === requestId) {
          setRefreshing(false);
        }
        if (
          generationRef.current === requestGeneration &&
          latestRequestRef.current === requestId
        ) {
          setLoading(false);
        }
      }
    },
    [accessToken, tokenScope],
  );

  /**
   * 외부(도착/이탈) 낙관적 갱신 — 세대를 증가시켜 진행 중인 이전 폴링 요청의 응답을
   * stale 로 표시한다.
   */
  const setPartyOptimistic = useCallback((next: WaitlistPartyData) => {
    if (!accessToken || activeScopeRef.current !== tokenScope || controllerRef.current?.signal.aborted) {
      return;
    }
    generationRef.current += 1;
    setParty(next);
    setStale(false);
    setError(null);
  }, [accessToken, tokenScope]);

  // 토큰 경계에서는 paint 전에 이전 팀을 비우고 이전 조회를 취소한다.
  useLayoutEffect(() => {
    // 토큰 변경(전환/등록/폐기) 시점에도 세대를 올려, 직전 토큰에 대한 응답이 뒤늦게
    // 와도 새 토큰의 상태를 덮어쓰지 못하게 한다.
    generationRef.current += 1;
    latestRequestRef.current += 1;

    activeScopeRef.current = tokenScope;
    pendingRequestsRef.current = new Set();
    notFoundRef.current = false;
    partyRef.current = null;
    setParty(null);
    setError(null);
    setNotFound(false);
    setStale(false);
    setRefreshing(false);

    if (!accessToken) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const controller = new AbortController();
    controllerRef.current = controller;
    void fetchParty(controller.signal);

    const timer = setInterval(() => {
      // 종결 상태는 더 이상 변하지 않는다.
      if (isTerminal(partyRef.current)) {
        clearInterval(timer);
        return;
      }
      // 404는 자동 재시도하지 않는다. 명시적 refresh 성공 시에만 다시 폴링한다.
      if (!notFoundRef.current && pendingRequestsRef.current.size === 0) {
        void fetchParty(controller.signal);
      }
    }, WAITLIST_POLL_INTERVAL_MS);

    return () => {
      generationRef.current += 1;
      latestRequestRef.current += 1;
      clearInterval(timer);
      controller.abort();
      controllerRef.current = null;
      activeScopeRef.current = null;
    };
  }, [accessToken, fetchParty, tokenScope]);

  const refresh = useCallback(() => fetchParty(), [fetchParty]);

  return { party, loading, error, stale, refreshing, notFound, setParty: setPartyOptimistic, refresh };
}
